import { assertUnreachable } from '../../common';
import {
  ActivateLeadEventData,
  AddPaymentInfoEventData,
  AddShippingInfoEventData,
  ApplyCouponEventData,
  BeginCheckoutEventData,
  ChangeConfigurationEventData,
  FeatureData,
  GenerateLeadEventData,
  ProductData,
  PurchaseEventData,
  SelectProductEventData,
  ViewConfigurationEventData,
  ViewConfigurationSummaryEventData,
  ViewProductEventData,
  ViewProductListEventData,
} from '../types';
import { GtmConfigurationItem, GtmEcommerceItem, GtmEvent, GtmEventName } from './types';

type Event =
  | { name: GtmEventName.ActivateLead; data: ActivateLeadEventData }
  | { name: GtmEventName.AddPaymentInfo; data: AddPaymentInfoEventData }
  | { name: GtmEventName.AddShippingInfo; data: AddShippingInfoEventData }
  | { name: GtmEventName.ApplyCoupon; data: ApplyCouponEventData }
  | { name: GtmEventName.BeginCheckout; data: BeginCheckoutEventData }
  | { name: GtmEventName.ConfigurationChange; data: ChangeConfigurationEventData }
  | { name: GtmEventName.ConfigurationSummary; data: ViewConfigurationSummaryEventData }
  | { name: GtmEventName.DefaultConfiguration; data: ViewConfigurationEventData }
  | { name: GtmEventName.GenerateLead; data: GenerateLeadEventData }
  | { name: GtmEventName.Purchase; data: PurchaseEventData }
  | { name: GtmEventName.SelectItem; data: SelectProductEventData }
  | { name: GtmEventName.ViewItem; data: ViewProductEventData }
  | { name: GtmEventName.ViewItemList; data: ViewProductListEventData };

export default class GtmEventBuilder {
  public static build(event: Event): GtmEvent {
    return GtmEventBuilder.clean(GtmEventBuilder.buildEvent(event));
  }

  private static buildEvent({ name, data }: Event): GtmEvent {
    switch (name) {
      case GtmEventName.ActivateLead:
        return {
          event: name,
          ecommerce: {
            currency: data.currency,
            item_name: data.product.name,
            item_purchasable: data.product.canBePurchased,
            items: [GtmEventBuilder.buildGtmItem(data.product)],
          },
        };
      case GtmEventName.GenerateLead:
        return {
          event: name,
          ecommerce: {
            consent_granted: data.consentGranted ? 'Yes' : 'No',
            country_name: data.countryName,
            currency: data.currency,
            customer_type: data.customerType,
            item_name: data.product.name,
            item_purchasable: data.product.canBePurchased,
            items: [GtmEventBuilder.buildGtmItem(data.product)],
          },
        };
      case GtmEventName.DefaultConfiguration:
        return {
          event: name,
          custom_event: {
            language_code: data.languageCode,
            item_id: data.productId,
            item_name: data.productName,
            item_purchasable: data.canBePurchased,
            items: data.features.map(GtmEventBuilder.buildGtmConfigurationItem),
          },
        };
      case GtmEventName.ConfigurationChange:
        return {
          event: name,
          custom_event: {
            language_code: data.languageCode,
            item_id: data.productId,
            item_name: data.productName,
            item_purchasable: data.canBePurchased,
            items: data.features.map(GtmEventBuilder.buildGtmConfigurationItem),
            feature_change: GtmEventBuilder.buildGtmConfigurationItem(data.targetFeature),
          },
        };
      case GtmEventName.ConfigurationSummary:
        return {
          event: name,
          custom_event: {
            language_code: data.languageCode,
            item_id: data.productId,
            item_name: data.productName,
            item_purchasable: data.canBePurchased,
            items: data.features.map(GtmEventBuilder.buildGtmConfigurationItem),
            configuration_code: data.configurationCode,
          },
        };
      case GtmEventName.ViewItemList:
        return {
          event: name,
          ecommerce: {
            currency: data.currency,
            item_list_name: data.listName,
            items: data.products.map(GtmEventBuilder.buildGtmItem),
          },
        };
      case GtmEventName.SelectItem:
        return {
          event: name,
          ecommerce: {
            currency: data.currency,
            item_list_name: data.listName,
            item_name: data.product.name,
            item_purchasable: data.product.canBePurchased,
            items: [GtmEventBuilder.buildGtmItem(data.product)],
          },
        };
      case GtmEventName.ViewItem:
        return {
          event: name,
          ecommerce: {
            currency: data.currency,
            value: data.value,
            item_name: data.product.name,
            item_purchasable: data.product.canBePurchased,
            items: [GtmEventBuilder.buildGtmItem(data.product)],
          },
        };
      case GtmEventName.BeginCheckout:
        return {
          event: name,
          ecommerce: {
            coupon: data.discountCode,
            currency: data.currency,
            value: data.value,
            // Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here
            item_name: data.products[0]?.name,
            item_purchasable: data.products[0]?.canBePurchased,
            items: data.products.map(GtmEventBuilder.buildGtmItem),
          },
        };
      case GtmEventName.ApplyCoupon:
        // It's a custom event, so data is in custom_event object instead of ecommerce object
        return {
          event: name,
          custom_event: {
            coupon: data.discountCode,
            currency: data.currency,
            value: data.value,
            // Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here
            item_name: data.products[0]?.name,
            item_purchasable: data.products[0]?.canBePurchased,
            items: data.products.map(GtmEventBuilder.buildGtmItem),
          },
        };
      case GtmEventName.AddShippingInfo:
        return {
          event: name,
          ecommerce: {
            shipping_tier: data.shippingTier,
            coupon: data.discountCode,
            currency: data.currency,
            value: data.value,
            // Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here
            item_name: data.products[0]?.name,
            item_purchasable: data.products[0]?.canBePurchased,
            items: data.products.map(GtmEventBuilder.buildGtmItem),
          },
        };
      case GtmEventName.AddPaymentInfo:
        return {
          event: name,
          ecommerce: {
            payment_type: data.paymentType,
            coupon: data.discountCode,
            currency: data.currency,
            value: data.value,
            // Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here
            item_name: data.products[0]?.name,
            item_purchasable: data.products[0]?.canBePurchased,
            items: data.products.map(GtmEventBuilder.buildGtmItem),
          },
        };
      case GtmEventName.Purchase:
        return {
          event: name,
          ecommerce: {
            transaction_id: data.orderNumber,
            tax: data.tax,
            shipping: data.shipping,
            coupon: data.discountCode,
            currency: data.currency,
            value: data.value,
            // Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here
            item_name: data.products[0]?.name,
            item_purchasable: data.products[0]?.canBePurchased,
            items: data.products.map(GtmEventBuilder.buildGtmItem),
          },
        };
      default:
        assertUnreachable(name);
    }
  }

  private static buildGtmConfigurationItem(feature: FeatureData): GtmConfigurationItem {
    return GtmEventBuilder.clean({
      feature_code: feature.code,
      feature_name: feature.name,
      feature_option_code: feature.optionCode,
      feature_option_name: feature.optionName,
    });
  }

  private static buildGtmItem(product: ProductData, index: number = 0): GtmEcommerceItem {
    return GtmEventBuilder.clean({
      // It is expected that the index starts at 1
      index: product.listPosition ?? index + 1,
      item_id: product.id,
      item_name: product.name,
      item_brand: product.brand,
      item_variant: product.configurationCode,
      item_category: product.category,
      item_list_name: product.listName,
      item_purchasable: product.canBePurchased,
      price: product.price,
      quantity: product.quantity,
      coupon: product.discountCode,
      discount: product.discount,
    });
  }

  private static clean<T>(obj: T): T {
    if (Array.isArray(obj)) {
      return obj.map((item) => GtmEventBuilder.clean(item)) as T;
    }

    if (obj && typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 0)
          .map(([k, v]) => [k, GtmEventBuilder.clean(v)]),
      ) as T;
    }

    return obj;
  }
}
